import { useCallback, useEffect, useRef, useState, type ClipboardEvent } from 'react';
import type { AuthorisationCodeRow } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { practiceService } from '@/core/services/practice';
import { AUTH_CODE_LENGTH, isAuthCodeInvalid, normalizeAuthCode, sanitizeAuthCode } from '@/core/utils/authCode';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { InputField } from '@/kit/FormField';
import { PageHeader, PageHeaderFooter } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import { SearchField } from './SearchField';
import { useDebouncedDistinct } from './useDebouncedDistinct';
import './AuthoriseCode.scss';

/** 0 all clients, 1 pending (no code saved), 2 code entered. */
const filterOptions = [
  { id: 0, name: 'All' },
  { id: 1, name: 'Pending only' },
  { id: 2, name: 'Entered only' },
];

/** Under a highlighted row and as the Save toast when any code is not a complete code. */
const INVALID_CODE_MESSAGE = `Enter ${AUTH_CODE_LENGTH} alphanumeric characters.`;

/**
 * Settings > Authorisation Codes (BUG-058, as the 3.0 screen): the grid is always editable (no Edit step - UX design
 * review); Cancel/Save sit persistently in the page header. Save stores every complete code (a code that is not
 * exactly 6 letters/numbers highlights its row and is not saved) and Cancel throws every edit away. A box takes 6
 * characters at most and nothing is cut silently; nothing is saved on blur or Tab.
 */
export function AuthoriseCode() {
  const [rows, setRows] = useState<AuthorisationCodeRow[]>([]);
  const [filter, setFilter] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  /** Rows whose typed code is not a complete code after a Save: highlighted, kept in the box, not saved. */
  const [invalidIds, setInvalidIds] = useState<Set<string>>(() => new Set());

  const rowsRef = useRef<AuthorisationCodeRow[]>([]);
  /** The code each row had when it was loaded, Edit was pressed or it was last saved: what Cancel restores. */
  const originalCodes = useRef(new Map<string, string | null>());
  const searchText = useRef('');
  const filterValue = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const replaceRows = useCallback((next: AuthorisationCodeRow[]) => {
    rowsRef.current = next;
    setRows(next);
  }, []);

  const patchRow = useCallback(
    (id: string, authCode: string | null) => {
      replaceRows(rowsRef.current.map((row) => (row.universalId === id ? { ...row, authCode } : row)));
    },
    [replaceRows],
  );

  const load = useCallback(() => {
    practiceService.getAuthorisationCodes(searchText.current, filterValue.current).then(
      (response) => {
        if (!mounted.current) return;
        replaceRows(response.items);
        originalCodes.current = new Map(response.items.map((row) => [row.universalId, row.authCode]));
        setInvalidIds(new Set());
      },
      (error) => notifyError(error),
    );
  }, [replaceRows]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = useDebouncedDistinct((text) => {
    searchText.current = text;
    // Reloading replaces the rows, so any edit in progress is replaced with the freshly loaded values.
    load();
  }, 300);

  const onFilterChange = (value: unknown) => {
    const next = Number(value);
    setFilter(next);
    filterValue.current = next;
    load();
  };

  const cancelEdit = () => {
    replaceRows(
      rowsRef.current.map((row) => ({ ...row, authCode: originalCodes.current.get(row.universalId) ?? null })),
    );
    setInvalidIds(new Set());
  };

  /**
   * The code is stored upper case and only ever holds the 6 characters Companies House issues. Reads the caret off
   * the input itself so a mid-string edit doesn't get bounced to the end of the field.
   */
  const onCodeInput = (row: AuthorisationCodeRow, input: HTMLInputElement) => {
    const caret = input.selectionStart ?? input.value.length;
    const sanitizedBeforeCaret = sanitizeAuthCode(input.value.slice(0, caret));
    const authCode = sanitizeAuthCode(input.value);
    patchRow(row.universalId, authCode);
    setInvalidIds((current) => {
      if (!current.has(row.universalId)) return current;
      const next = new Set(current);
      next.delete(row.universalId);
      return next;
    });
    const newCaret = Math.min(sanitizedBeforeCaret.length, authCode.length);
    queueMicrotask(() => input.setSelectionRange(newCaret, newCaret));
  };

  /**
   * A paste longer than 6 characters is cut to 6 IN THE BOX, before anything is saved, so the user sees exactly what
   * Save will store (nothing is cut silently afterwards).
   */
  const onCodePaste = (row: AuthorisationCodeRow, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const input = event.currentTarget;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const pasted = event.clipboardData.getData('text');
    input.value = input.value.slice(0, start) + pasted + input.value.slice(end);
    onCodeInput(row, input);
  };

  const save = async () => {
    if (isSaving) return;
    const changed = rowsRef.current.filter(
      (row) =>
        normalizeAuthCode(row.authCode) !== normalizeAuthCode(originalCodes.current.get(row.universalId) ?? null),
    );
    const invalid = changed.filter((row) => isAuthCodeInvalid(row.authCode));
    const valid = changed.filter((row) => !isAuthCodeInvalid(row.authCode));
    setInvalidIds(new Set(invalid.map((row) => row.universalId)));

    if (changed.length === 0) {
      // Test case 3195: Save with nothing changed says so instead of silently closing edit mode.
      notifyError('Please enter auth code to update.');
      return;
    }

    setIsSaving(true);
    let failed = 0;
    const saved = await Promise.all(
      valid.map((row) => {
        const code = normalizeAuthCode(row.authCode);
        return practiceService.saveAuthorisationCode(row.universalId, code).then(
          () => {
            originalCodes.current.set(row.universalId, code);
            if (mounted.current) patchRow(row.universalId, code);
            return true;
          },
          (error) => {
            failed += 1;
            notifyError(error);
            return false;
          },
        );
      }),
    );
    if (!mounted.current) return;
    setIsSaving(false);

    if (invalid.length > 0) {
      // The saved rows are reported first so the problem rows are what the user is left looking at.
      if (saved.some(Boolean)) notifySuccess('Codes saved successfully.');
      // Report 1 #8 (TC 3196): the wording the highlighted rows carry (and Company Info's refusal), so the toast and
      // the row say the same thing. 3.0's own validator text ("Enter valid auth code") was hidden behind the highlight.
      notifyError(INVALID_CODE_MESSAGE);
      return;
    }
    if (failed > 0) return;
    if (valid.length > 0) notifySuccess('Codes saved successfully.');
  };

  const columns: DataTableColumn<AuthorisationCodeRow>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span className="onelineclamp" title={row.name}>
            {row.name}
          </span>
        </div>
      ),
    },
    {
      id: 'register',
      header: 'Registration No.',
      cell: (row) => <div className="white-space-nowrap">{row.registrationNo}</div>,
    },
    {
      id: 'utr',
      header: 'UTR No.',
      cell: (row) => <div className="white-space-nowrap">{row.utrNo}</div>,
    },
    {
      id: 'authcode',
      header: 'Auth. Code',
      cell: (row) => (
        <>
          <InputField
            fieldClassName="w-200 auth-code-field"
            autoComplete="off"
            maxLength={AUTH_CODE_LENGTH}
            placeholder="6 characters"
            invalid={invalidIds.has(row.universalId)}
            value={row.authCode ?? ''}
            onChange={(event) => onCodeInput(row, event.currentTarget)}
            onPaste={(event) => onCodePaste(row, event)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') cancelEdit();
            }}
          />
          {invalidIds.has(row.universalId) && <div className="text-red font-size-12">{INVALID_CODE_MESSAGE}</div>}
        </>
      ),
    },
  ];

  return (
    <div className="form wp-100 settings-authorise-code">
      {/* UX design review: no Edit step - the grid is always editable, Cancel/Save sit persistently in a sticky footer. */}
      <PageHeader title="Authorisation Codes" mode="edit" actionsPlacement="footer" busy={isSaving} onSave={() => void save()} onCancel={cancelEdit} />
      <div className="content-body">
        <div className="info-message warning-message mb-1">
          Manage company authorisation codes (for all limited type companies).
        </div>
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <SearchField onSearch={(text) => onSearch(text.trim())} />
            <Select
              className="w-150"
              items={filterOptions}
              bindLabel="name"
              bindValue="id"
              value={filter}
              clearable={false}
              searchable={false}
              onChange={onFilterChange}
            />
          </div>
        </div>
        <div className="mat-table-wrapper no-hover">
          <DataTable stickyHeader columns={columns} rows={rows} rowKey={(row) => row.universalId} />
        </div>
      </div>
      <PageHeaderFooter mode="edit" busy={isSaving} onSave={() => void save()} onCancel={cancelEdit} />
    </div>
  );
}
